import { webPageSchema } from "@/lib/schema";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Star, Quote } from "lucide-react";
import { NAP, MAIN_PHONE, waLink } from "@/lib/whatsapp";
import { ogImageMeta } from "@/lib/og-image";
import {
  GOOGLE_RATING,
  GOOGLE_REVIEW_COUNT,
  testimonios,
  reviewSchema,
} from "@/lib/testimonials";
import { PageHero } from "@/components/site/PageHero";
import { PanelSection } from "@/components/site/PanelSection";
import { PageCta } from "@/components/site/PageCta";

const TITLE = "Testimonios reales de familias | Residencia Oasis Cuernavaca";
const DESCRIPTION =
  "Opiniones reales de familias que confiaron el cuidado de sus padres a Residencia Oasis Cuernavaca. Calificación 4.9★ con 68 reseñas en Google.";

export const Route = createFileRoute("/testimonios")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://oasiscuernavaca.com/testimonios" },
      ...ogImageMeta(),
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESCRIPTION },
    ],
    links: [{ rel: "canonical", href: "https://oasiscuernavaca.com/testimonios" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(
          webPageSchema({ name: "Testimonios de familias de Residencia Oasis Cuernavaca", description: DESCRIPTION, path: "/testimonios", type: "WebPage" }),
        ),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": ["LocalBusiness", "AssistedLivingFacility"],
          name: NAP.name,
          telephone: MAIN_PHONE.tel,
          address: {
            "@type": "PostalAddress",
            streetAddress: NAP.street,
            addressLocality: NAP.locality,
            addressRegion: NAP.region,
            postalCode: NAP.postalCode,
            addressCountry: NAP.country,
          },
          aggregateRating: {
            "@type": "AggregateRating",
            ratingValue: String(GOOGLE_RATING),
            reviewCount: String(GOOGLE_REVIEW_COUNT),
            bestRating: "5",
          },
          review: reviewSchema,
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Inicio", item: "/" },
            { "@type": "ListItem", position: 2, name: "Testimonios", item: "/testimonios" },
          ],
        }),
      },
    ],
  }),
  component: Page,
});

function Stars({ n, label }: { n: number; label: string }) {
  return (
    <span className="flex gap-0.5" role="img" aria-label={label}>
      {Array.from({ length: n }).map((_, i) => (
        <Star
          key={i}
          className="h-4 w-4 fill-rating text-rating"
          aria-hidden="true"
        />
      ))}
    </span>
  );
}

function Page() {
  const wa = waLink("testimonios", "quiero agendar una visita");

  return (
    <>
      <PageHero
        breadcrumb="Testimonios"
        title="Lo que dicen las familias de Residencia Oasis Cuernavaca"
        answer="Somos una residencia boutique con capacidad limitada: eso nos permite conocer a cada residente y dar un seguimiento real, no genérico. Estas son las opiniones de las familias que ya confiaron en nosotros."
        waHref={wa}
        waLabel="Platiquemos por WhatsApp"
      >
        <a
          href={NAP.googleReviewsUrl}
          target="_blank"
          rel="noopener nofollow"
          className="mt-6 inline-flex min-h-11 flex-wrap items-center gap-3 rounded-full border border-border bg-background px-5 py-2 font-semibold shadow-sm hover:bg-secondary"
        >
          <span className="font-display text-[length:var(--text-title)] leading-none">{GOOGLE_RATING}</span>
          <Stars n={5} label={`${GOOGLE_RATING} de 5 estrellas`} />
          <span className="font-normal text-muted-foreground">
            {GOOGLE_REVIEW_COUNT} opiniones en Google
          </span>
        </a>
        <p className="mt-4 text-muted-foreground">
          Visitas sin costo ni compromiso: conozca las instalaciones, hable con el equipo y
          resuelva todas sus dudas.{" "}
          <Link to="/contacto" className="underline">
            Venga a conocer la casa
          </Link>
          .
        </p>
      </PageHero>

      <section className="section-air bg-[var(--surface-beige)]">
        <div className="container-site">
          <blockquote className="pull-quote mx-auto max-w-3xl text-center">
            <p className="quote-text">Hogar de privilegio, amor y cariño.</p>
            <footer className="mt-4 font-sans text-[1rem] not-italic text-muted-foreground">
              — Don Moises Maislin, residente de Oasis
            </footer>
          </blockquote>
        </div>
      </section>

      <PanelSection tone="soft" eyebrow="Opiniones" title="Reseñas de familias e hijos tutores">
        <div className="mt-2 grid gap-6 md:grid-cols-2">
          {testimonios.map((t) => (
            <figure key={t.nombre} className="card-warm">
              <Quote className="h-6 w-6 text-primary/60" aria-hidden="true" />
              <div className="mt-3">
                <Stars n={t.estrellas} label={`${t.estrellas} de 5 estrellas`} />
              </div>
              <blockquote className="mt-3">“{t.texto}”</blockquote>
              <figcaption className="mt-4 font-semibold text-primary">
                {t.nombre}{" "}
                <span className="font-normal text-muted-foreground">· {t.relacion}</span>
              </figcaption>
            </figure>
          ))}
        </div>
      </PanelSection>

      <PageCta
        waHref={wa}
        waLabel="Escribir por WhatsApp"
        eyebrow="Compruébelo"
        title="¿Quiere ver con sus propios ojos lo que ellas cuentan?"
        answer="Agende una visita y compruebe el trato, la limpieza y el ambiente. Desde $950 MXN al día, según el nivel de acompañamiento y cuidados requeridos."
      />
    </>
  );
}
